import {
  CanvasTexture,
  Group,
  MeshPhysicalMaterial,
  RepeatWrapping,
  SRGBColorSpace,
  type Material,
  type Texture,
} from 'three';
import type { Tile } from '../../engine/tile.js';
import { uvForTile } from './tile-faces.js';
import {
  assembleTileMesh,
  buildBodyGeometry,
  buildFaceGeometry,
  buildSideGradientTexture,
  setTileDim as kitSetTileDim,
  type TileDims,
} from './tile-kit.js';

/**
 * Mahjong tile mesh — a beveled rounded brick (1 × 1.32 × 0.30) with a
 * printed face plate inlaid on top. Aspect (W : H : D ≈ 1 : 1.32 : 0.22)
 * is flatter than real 22×30×18 mm tiles to match Microsoft Mahjong / KMahjongg
 * UI convention so faces stay readable from the locked top-down camera.
 *
 * The geometry + side-AO trick + assembly + dim helper come from
 * `tile-kit.ts`. This module owns the things specific to mahjong:
 *   - bone-grain caps texture (the warm cream "ivory" with subtle streaks)
 *   - polished mahjong material values (clearcoat, sheen)
 *   - face material that pulls a per-tile cell from the shared atlas
 */

export const TILE_W = 1.0;
export const TILE_H = 1.32;
export const TILE_D = 0.30;
// Tiles in the same row/column placed `TILE_PITCH_*` apart, leaving a
// visible gap so adjacent edges read as separate objects.
export const TILE_PITCH_X = TILE_W + 0.045;
export const TILE_PITCH_Z = TILE_H + 0.045;

const DIMS: TileDims = {
  sizeX: TILE_W,
  sizeZ: TILE_H,
  depth: TILE_D,
  cornerRadius: 0.075,
  bevel: 0.024,
};
const FACE_LIFT = 0.0025;

let _bodyGeom: ReturnType<typeof buildBodyGeometry> | null = null;
function bodyGeom() {
  if (!_bodyGeom) _bodyGeom = buildBodyGeometry(DIMS);
  return _bodyGeom;
}

let _faceGeom: ReturnType<typeof buildFaceGeometry> | null = null;
function faceGeom() {
  if (!_faceGeom) _faceGeom = buildFaceGeometry(DIMS);
  return _faceGeom;
}

let _sideGradient: CanvasTexture | null = null;
function sideGradient() {
  if (!_sideGradient) _sideGradient = buildSideGradientTexture();
  return _sideGradient;
}

// ── Bone-grain caps texture (mahjong-specific) ────────────────────────
// Warm ivory base + faint horizontal streaks + per-pixel speckle so close-
// up reads as polished bone, not flat paint.
let _boneTex: CanvasTexture | null = null;
function boneTexture(): CanvasTexture {
  if (_boneTex) return _boneTex;
  const SIZE = 512;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  const base = ctx.createLinearGradient(0, 0, SIZE, SIZE);
  base.addColorStop(0, '#f5e9c9');
  base.addColorStop(1, '#e6d5a8');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, SIZE, SIZE);
  ctx.globalAlpha = 0.085;
  for (let i = 0; i < 18; i++) {
    ctx.strokeStyle = i % 2 === 0 ? '#b89b6c' : '#fff2cf';
    ctx.lineWidth = 1 + Math.random() * 1.6;
    ctx.beginPath();
    const y = Math.random() * SIZE;
    const wobble = 16;
    ctx.moveTo(-10, y + (Math.random() - 0.5) * wobble);
    ctx.bezierCurveTo(
      SIZE * 0.33, y + (Math.random() - 0.5) * wobble,
      SIZE * 0.66, y + (Math.random() - 0.5) * wobble,
      SIZE + 10, y + (Math.random() - 0.5) * wobble,
    );
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  const img = ctx.getImageData(0, 0, SIZE, SIZE);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (Math.random() - 0.5) * 10;
    img.data[i]     = Math.max(0, Math.min(255, img.data[i]! + n));
    img.data[i + 1] = Math.max(0, Math.min(255, img.data[i + 1]! + n * 0.85));
    img.data[i + 2] = Math.max(0, Math.min(255, img.data[i + 2]! + n * 0.6));
  }
  ctx.putImageData(img, 0, 0);
  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.wrapS = tex.wrapT = RepeatWrapping;
  tex.anisotropy = 8;
  _boneTex = tex;
  return tex;
}

// ── Material templates (cloned per-tile so dim can be applied) ────────
let _capsMat: MeshPhysicalMaterial | null = null;
function capsMaterial(): MeshPhysicalMaterial {
  if (_capsMat) return _capsMat;
  _capsMat = new MeshPhysicalMaterial({
    color: 0xffffff,           // texture provides hue
    map: boneTexture(),
    roughness: 0.46,
    metalness: 0,
    clearcoat: 0.35,
    clearcoatRoughness: 0.5,
    sheen: 0.18,
    sheenColor: 0xfff5d8,
    sheenRoughness: 0.55,
  });
  return _capsMat;
}

let _sideMat: MeshPhysicalMaterial | null = null;
function sideMaterial(): MeshPhysicalMaterial {
  if (_sideMat) return _sideMat;
  // Warm tan, ~22% darker than the cap face — adjacent tiles' edges
  // separate cleanly from a near-top-down view.
  _sideMat = new MeshPhysicalMaterial({
    color: 0xc9b483,
    map: sideGradient(),
    roughness: 0.62,
    metalness: 0,
    clearcoat: 0.18,
    clearcoatRoughness: 0.6,
  });
  return _sideMat;
}

function cloneAtlas(atlas: Texture, offset: readonly [number, number], repeat: readonly [number, number]): Texture {
  const t = atlas.clone();
  t.needsUpdate = true;
  t.offset.set(offset[0], offset[1]);
  t.repeat.set(repeat[0], repeat[1]);
  return t;
}

export function createFaceMaterial(atlas: Texture, tile: Tile): Material {
  const { offset, repeat } = uvForTile(tile);
  const map = cloneAtlas(atlas, offset, repeat);
  return new MeshPhysicalMaterial({
    color: 0xffffff,
    map,
    roughness: 0.34,
    metalness: 0,
    clearcoat: 0.45,
    clearcoatRoughness: 0.28,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

export function createTileMesh(faceMaterial: Material): Group {
  const caps = capsMaterial().clone();
  const sides = sideMaterial().clone();
  return assembleTileMesh({
    bodyGeom: bodyGeom(),
    faceGeom: faceGeom(),
    dims: DIMS,
    faceLift: FACE_LIFT,
    capsMat: caps,
    sidesMat: sides,
    faceMat: faceMaterial,
  });
}

export const setTileDim = kitSetTileDim;
